import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/apiClient";

const field = "bg-transparent border-0 border-b border-brand-navy/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold";

export default function Contact() {
  const { content = {} } = useOutletContext() || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail ? String(err.response.data.detail) : "Something went wrong.");
    } finally { setLoading(false); }
  };

  const mapAddr = encodeURIComponent(content.address || "Gandhi Nagar, Jammu, Jammu and Kashmir, India");

  return (
    <div data-testid="contact-page">
      <section className="relative pt-32 pb-14 bg-brand-navy grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="overline mb-3">Get In Touch</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">Contact Us</h1>
          <p className="mt-4 text-white/70 max-w-xl">We’d love to hear about your event. Reach out and our team will respond promptly.</p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <h2 className="font-serif text-3xl text-brand-navy">Send us a message</h2>
          <form onSubmit={submit} data-testid="contact-form" className="mt-8 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Name</Label><Input required data-testid="contact-name" value={form.name} onChange={set("name")} className={field} /></div>
              <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={field} /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Email</Label><Input required type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className={field} /></div>
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Subject</Label><Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={field} /></div>
            <div><Label className="text-xs uppercase tracking-widest text-brand-ink/60">Message</Label><Textarea required rows={4} data-testid="contact-message" value={form.message} onChange={set("message")} className={field} /></div>
            <button disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-brand-navy text-white font-semibold px-8 py-3.5 rounded-sm hover:bg-brand-blue transition-colors disabled:opacity-60">
              {loading && <Loader2 size={18} className="animate-spin" />} Send Message
            </button>
          </form>
        </div>

        <div className="space-y-6">
          {[
            { icon: Phone, label: "Call Us", value: `${content.phone || "+91 70065 34892"}${content.phone2 ? " / " + content.phone2 : ""}`, href: `tel:${(content.phone||"").replace(/\s/g,"")}` },
            { icon: Mail, label: "Email", value: content.email || "hello@weddingevents.in", href: `mailto:${content.email||"hello@weddingevents.in"}` },
            { icon: MapPin, label: "Visit", value: content.address || "Gandhi Nagar, Jammu, J&K, India" },
          ].map((c) => (
            <a key={c.label} href={c.href || "#"} className="flex gap-4 border border-brand-navy/10 p-5 rounded-sm bg-white hover:border-brand-gold transition-colors">
              <c.icon className="text-brand-gold shrink-0" size={22} />
              <div><p className="overline">{c.label}</p><p className="text-brand-ink/85 mt-1">{c.value}</p></div>
            </a>
          ))}
          <div className="rounded-sm overflow-hidden border border-brand-navy/10 h-64">
            <iframe title="location" data-testid="contact-map" className="w-full h-full" loading="lazy" src={`https://www.google.com/maps?q=${mapAddr}&output=embed`} />
          </div>
        </div>
      </section>
    </div>
  );
}
